import { useState, useEffect, useCallback } from 'react';
import type {
  CustomerAlias,
  CreateCustomerAliasRequest,
  UpdateCustomerAliasRequest,
} from '@techgitter/contracts';
import {
  getCustomerAliases,
  createCustomerAlias,
  updateCustomerAlias,
  deleteCustomerAlias,
} from '../services/customerAliasService';

export interface UseCustomerAliasesResult {
  aliases: CustomerAlias[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  create: (payload: CreateCustomerAliasRequest) => Promise<void>;
  update: (aliasId: string, payload: UpdateCustomerAliasRequest) => Promise<void>;
  remove: (aliasId: string) => Promise<void>;
}

export function useCustomerAliases(
  dataverseCustomerId: string | null
): UseCustomerAliasesResult {
  const [aliases, setAliases] = useState<CustomerAlias[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadAliases = useCallback(async (signal?: AbortSignal) => {
    if (!dataverseCustomerId) {
      setAliases([]);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCustomerAliases(dataverseCustomerId, signal);
      if (signal?.aborted) return;
      setAliases(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load aliases');
      setAliases([]);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [dataverseCustomerId]);

  useEffect(() => {
    const controller = new AbortController();
    loadAliases(controller.signal);
    return () => controller.abort();
  }, [loadAliases]);

  const refresh = useCallback(() => {
    loadAliases();
  }, [loadAliases]);

  const create = useCallback(async (payload: CreateCustomerAliasRequest) => {
    if (!dataverseCustomerId) throw new Error('No customer selected');
    await createCustomerAlias(dataverseCustomerId, payload);
    await loadAliases();
  }, [dataverseCustomerId, loadAliases]);

  const update = useCallback(async (aliasId: string, payload: UpdateCustomerAliasRequest) => {
    if (!dataverseCustomerId) throw new Error('No customer selected');
    await updateCustomerAlias(dataverseCustomerId, aliasId, payload);
    await loadAliases();
  }, [dataverseCustomerId, loadAliases]);

  const remove = useCallback(async (aliasId: string) => {
    if (!dataverseCustomerId) throw new Error('No customer selected');
    await deleteCustomerAlias(dataverseCustomerId, aliasId);
    await loadAliases();
  }, [dataverseCustomerId, loadAliases]);

  return { aliases, isLoading, error, refresh, create, update, remove };
}
